import React, { useCallback, useMemo, useRef, useState } from "react";
import { SelectTag } from "@bloomberg/intercom";
import { IconButton } from "@bloomberg/media-ui-core";
import { Exit, Tune } from "@bloomberg/media-ui-icons";
import { SubscriberOnlyBenefitOverlay } from "@bloomberg/media-ui-subscriptions";
import { useRegUIClient } from "@foundation/reg-ui-client";
import { trackClickEvent } from "@foundation/nextjs-gtm";

import { PickerProps } from "./types";
import styles from "./tagPicker.module.css";

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};
const MAX_FOLLOWED_TOPICS = 12;

const trackClick = (eventAction: string, eventLabel: string) =>
  trackClickEvent(
    "lineup_module_click",
    eventLabel,
    eventAction,
    "topic_module"
  );

const TagPicker = ({
  description,
  label,
  selected,
  itemGroups,
  onClose = noop,
  onSelect = noop,
  onUnselect = noop,
  isSignedIn,
}: PickerProps) => {
  const tagPickerRef = useRef(null);
  const [isSubscriberBenefitOpen, setIsSubscriberBenefitOpen] = useState(false);
  const { client } = useRegUIClient();

  const totalTopicsFollowedByUser = useMemo(() => {
    return Object.keys(selected).length;
  }, [selected]);

  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOnClick = useCallback(
    (id: string, isSelected: boolean) => {
      if (!isSignedIn) {
        setIsSubscriberBenefitOpen(true);
        trackClick("open", "subscribe_tout");

        return;
      }

      if (isSelected) {
        onUnselect(id);
      } else {
        onSelect(id);
      }
    },
    [isSignedIn, onSelect, onUnselect]
  );

  const handleOnExit = useCallback(() => {
    trackClick("close", "subscribe_tout");
    setIsSubscriberBenefitOpen(false);
  }, [setIsSubscriberBenefitOpen]);

  return (
    <div className={styles.tagPicker} ref={tagPickerRef}>
      <div className={styles.headerContainer}>
        <div className={styles.labelContainer}>
          <Tune className={styles.icon} />
          <span className={styles.label}>{label}</span>
        </div>
        <IconButton
          className={styles.exit}
          onClick={handleOnClose}
          Icon={<Exit />}
          version="phoenix"
        />
      </div>
      <p className={styles.description}>{description}</p>
      <div className={styles.divider} />
      {itemGroups.map(({ title: groupTitle, items }, index) => {
        return (
          <div className={styles.groupContainer} key={groupTitle}>
            <p className={styles.groupTitle}>{groupTitle}</p>
            <div className={styles.tagContainer}>
              {Object.entries(items).map(([key, { label: itemLabel }]) => {
                const isSelected = selected[key] ?? false;

                return (
                  <div
                    className={!isSelected ? styles.unselectedTag : undefined}
                    key={key}
                  >
                    <SelectTag.Component
                      onClick={() => handleOnClick(key, isSelected)}
                      text={itemLabel}
                      isSelected={isSelected}
                      isDisabled={
                        !isSelected &&
                        totalTopicsFollowedByUser >= MAX_FOLLOWED_TOPICS
                      }
                    />
                  </div>
                );
              })}
            </div>
            {index < itemGroups.length - 1 && (
              <div className={styles.divider} />
            )}
          </div>
        );
      })}
      {isSubscriberBenefitOpen && (
        <SubscriberOnlyBenefitOverlay
          anchorElementRef={tagPickerRef}
          client={client!}
          inSource="stockspage-topic-module"
          onExit={handleOnExit}
          placement="right-start"
        >
          Sign in to follow topics and surface the content most relevant to you
        </SubscriberOnlyBenefitOverlay>
      )}
    </div>
  );
};

export default TagPicker;
